import { Text, Stack, Box, Checkbox, Alert, Switch, SegmentedControl, Tooltip, Group } from '@mantine/core';
import { modals } from '@mantine/modals';
import { AlertCircle, Info } from 'react-feather';
import type { ModalitiesStepProps } from './types';
import { useFeatureFlag } from '../../../stores/featureFlags';
import {
  MODALITY_OPTIONS,
  COACHING_SUB_TYPE_OPTIONS,
  LIVE_SESSION_DELIVERY_LABEL,
  LIVE_SESSION_DELIVERY_TOOLTIP,
  LIVE_SESSION_DELIVERY_OFF_CONFIRM,
} from '../../../config/cohortConfig';

export function ModalitiesStep({ form, onToggleModality }: ModalitiesStepProps) {
  const hasSelection = form.values.selectedModalities.length > 0;
  // MED-948 (E1.10a) AC11 — the Live Session Delivery gate (and the GC AI toggle
  // it reveals) only exist when the system-level live_session_delivery flag is
  // on. useFeatureFlag fails closed (false until loaded / on error).
  const liveSessionDeliveryFlag = useFeatureFlag('live_session_delivery');

  // MED-948 AC8 — turning the gate off while Group Coaching AI features are
  // enabled prompts for confirmation, then resets AI features to off (not just
  // hidden) so no stale "enabled-but-hidden" state is submitted.
  const handleLiveSessionDeliveryToggle = (checked: boolean) => {
    if (!checked && form.values.groupCoachingConfig.aiFeaturesEnabled) {
      modals.openConfirmModal({
        title: 'Turn off Live Session Delivery in SGOS?',
        children: <Text size="sm">{LIVE_SESSION_DELIVERY_OFF_CONFIRM}</Text>,
        labels: { confirm: 'Turn off', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          form.setFieldValue('groupCoachingConfig.liveSessionDeliveryEnabled', false);
          form.setFieldValue('groupCoachingConfig.aiFeaturesEnabled', false);
        },
      });
      return;
    }
    form.setFieldValue('groupCoachingConfig.liveSessionDeliveryEnabled', checked);
    // Defensive: turning the gate off always clears AI features (AC4).
    if (!checked) {
      form.setFieldValue('groupCoachingConfig.aiFeaturesEnabled', false);
    }
  };

  return (
    <Stack gap="lg">
      <Box>
        <Text size="lg" fw={600}>Select Modalities</Text>
        <Text size="sm" c="dimmed" mt="xs">
          Choose the coaching and learning modalities for this cohort.
        </Text>
      </Box>

      {!hasSelection && form.errors.selectedModalities && (
        <Alert icon={<AlertCircle size={16} />} color="red" variant="light">
          {form.errors.selectedModalities}
        </Alert>
      )}

      <Stack gap="sm">
        {MODALITY_OPTIONS.map((option) => {
          const isSelected = form.values.selectedModalities.includes(option.value);

          // MED-848 — only Group Coaching shows sub-type. 1:1 Coaching shows no
          // facilitation controls.
          const isGroupCoaching = isSelected && option.value === 'group_coaching';
          const isWorkshop = isSelected && option.value === 'workshop';
          const showSubType = isGroupCoaching;

          // MED-948 (E1.10a) — Live Session Delivery gate is Group-Coaching-only
          // (AC9/AC10) and only when the system flag is on (AC11).
          const showLiveSessionDelivery = isGroupCoaching && liveSessionDeliveryFlag;
          // GC AI toggle is now revealed by the gate (AC1/AC3/AC4). Workshop's AI
          // toggle stays independent and ungated (AC10; Workshop unchanged).
          const showGroupCoachingAiToggle =
            showLiveSessionDelivery && form.values.groupCoachingConfig.liveSessionDeliveryEnabled;
          const showWorkshopAiToggle = isWorkshop;
          // MED-1069 (E1.10c) — Commitment Tracking is Group-Coaching-only (AC6)
          // and gated by the same system flag as the rest of the surface (AC12),
          // but INDEPENDENT of the Live Session Delivery toggle (AC7) — unlike the
          // GC AI toggle, it does NOT require live delivery to be on.
          const showCommitmentTracking = isGroupCoaching && liveSessionDeliveryFlag;

          const subTypeError = form.errors['groupCoachingConfig.coachingSubType'];
          const subTypeValue = form.values.groupCoachingConfig.coachingSubType;

          return (
            <Box
              key={option.value}
              p="md"
              style={{
                cursor: 'pointer',
                borderRadius: '8px',
                border: isSelected ? '2px solid #28463A' : '2px solid #E8E6E3',
                backgroundColor: isSelected ? '#F9F8F6' : 'white',
                transition: 'all 0.15s ease',
              }}
              onClick={() => onToggleModality(option.value)}
            >
              <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleModality(option.value)}
                  // MED-1350: the card also toggles on click. Without this the
                  // checkbox click toggled twice and changed nothing.
                  onClick={(e) => e.stopPropagation()}
                  size="md"
                  color="medleyGreen"
                  styles={{ input: { cursor: 'pointer' } }}
                  mt={2}
                />

                <Box flex={1}>
                  <Text fw={600} size="sm">
                    {option.label}
                  </Text>
                  <Text size="xs" c="dimmed" mt={4}>
                    {option.description}
                  </Text>

                  {(showSubType ||
                    showLiveSessionDelivery ||
                    showWorkshopAiToggle) && (
                    <Stack
                      gap="md"
                      mt="md"
                      pt="md"
                      style={{ borderTop: '1px solid #E8E6E3' }}
                      // Stop card-level click handler from toggling the
                      // modality off when the user interacts with controls.
                      onClick={(e) => e.stopPropagation()}
                    >
                      {showSubType && (
                        <Box>
                          <Text size="xs" fw={600} mb="xs">
                            Coaching sub-type
                            <Text component="span" c="red" ml={4}>*</Text>
                          </Text>
                          <SegmentedControl
                            value={subTypeValue ?? ''}
                            onChange={(value) =>
                              form.setFieldValue(
                                'groupCoachingConfig.coachingSubType',
                                value as 'coach_led' | 'peer_led',
                              )
                            }
                            data={COACHING_SUB_TYPE_OPTIONS.map((o) => ({
                              value: o.value,
                              label: o.label,
                            }))}
                            color="medleyGreen"
                            fullWidth
                          />
                          <Text size="xs" c="dimmed" mt={4}>
                            {subTypeValue
                              ? COACHING_SUB_TYPE_OPTIONS.find((o) => o.value === subTypeValue)
                                  ?.description
                              : 'Select how Group Coaching sessions are led.'}
                          </Text>
                          {!subTypeValue && subTypeError && (
                            <Text size="xs" c="red" mt={4}>
                              {subTypeError}
                            </Text>
                          )}
                        </Box>
                      )}

                      {/* MED-948 (E1.10a) — Live Session Delivery gate, shown
                          before the AI toggle for Group Coaching (AC1). */}
                      {showLiveSessionDelivery && (
                        <Switch
                          color="medleyGreen"
                          label={
                            <Group gap={6} wrap="nowrap" align="center">
                              <span>{LIVE_SESSION_DELIVERY_LABEL}</span>
                              <Tooltip
                                label={LIVE_SESSION_DELIVERY_TOOLTIP}
                                multiline
                                w={260}
                                withArrow
                              >
                                <Box
                                  component="span"
                                  style={{ display: 'inline-flex', cursor: 'help', color: '#868E96' }}
                                  aria-label={LIVE_SESSION_DELIVERY_TOOLTIP}
                                >
                                  <Info size={14} />
                                </Box>
                              </Tooltip>
                            </Group>
                          }
                          checked={form.values.groupCoachingConfig.liveSessionDeliveryEnabled}
                          onChange={(event) =>
                            handleLiveSessionDeliveryToggle(event.currentTarget.checked)
                          }
                        />
                      )}

                      {/* GC AI toggle — only when the gate is on (AC3/AC4). */}
                      {showGroupCoachingAiToggle && (
                        <Switch
                          color="medleyGreen"
                          label="AI features"
                          description="Enable AI-powered facilitation features (pre-session synthesis, real-time nudges, post-session distillation) for this modality."
                          checked={form.values.groupCoachingConfig.aiFeaturesEnabled}
                          onChange={(event) =>
                            form.setFieldValue(
                              'groupCoachingConfig.aiFeaturesEnabled',
                              event.currentTarget.checked,
                            )
                          }
                        />
                      )}

                      {/* MED-1069 (E1.10c) — Commitment Tracking for Group
                          Coaching. INDEPENDENT of the Live Session Delivery gate
                          (AC7): rendered whenever the feature flag is on, not
                          nested under live delivery. Defaults off (AC7). */}
                      {showCommitmentTracking && (
                        <Switch
                          color="medleyGreen"
                          label="Commitment Tracking"
                          description="Lets participants set a commitment each session and reflect on the prior one. Off by default."
                          checked={form.values.groupCoachingConfig.commitmentTrackingEnabled}
                          onChange={(event) =>
                            form.setFieldValue(
                              'groupCoachingConfig.commitmentTrackingEnabled',
                              event.currentTarget.checked,
                            )
                          }
                        />
                      )}

                      {/* Workshop AI toggle — independent of the gate (AC10). */}
                      {showWorkshopAiToggle && (
                        <Switch
                          color="medleyGreen"
                          label="AI features"
                          description="Enable AI-powered facilitation features (pre-session synthesis, real-time nudges, post-session distillation) for this modality."
                          checked={form.values.workshopConfig.aiFeaturesEnabled}
                          onChange={(event) =>
                            form.setFieldValue(
                              'workshopConfig.aiFeaturesEnabled',
                              event.currentTarget.checked,
                            )
                          }
                        />
                      )}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Text size="xs" c="dimmed">
        At least one modality is required.
      </Text>
    </Stack>
  );
}
